import { ProNavigation } from "@/components/ProNavigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

const essentialOils = [
  {
    id: "marjolaine",
    name: "Marjolaine",
    latinName: "Origanum majorana",
    emoji: "🌿",
    properties: [
      "Neurotonique, rééquilibrant nerveux",
      "Anti-asthénique",
      "Stimulant digestif",
      "Antispasmodique",
      "Tonique circulatoire",
      "Antalgique",
      "Régulateur respiratoire",
    ],
    indications: [
      "Stress, anxiété, surmenage nerveux",
      "Insomnie",
      "Spasmes digestifs, dyspepsies",
      "Tensions musculaires",
      "Troubles respiratoires légers",
    ],
    contraindications: ["Grossesse + allaitement", "Enfants < 6 ans", "Prudence en cas d'hypotension"],
    internalUse:
      "1 goutte dans miel ou comprimé neutre, 2×/jour pendant 5 jours. Indiqué pour stress ou spasmes digestifs.",
    externalUse:
      "Dilution 20% dans une huile végétale. Massage plexus solaire, abdomen, nuque. Très efficace en massage pour calmer le système nerveux.",
  },
  {
    id: "romarin",
    name: "Romarin",
    latinName: "Rosmarinus officinalis",
    emoji: "🌱",
    properties: [
      "Mucolytique, expectorant",
      "Anti-infectieux",
      "Tonique musculaire, veineux et cérébral",
      "Régulateur hépatique",
      "Antalgique",
    ],
    indications: [
      "Bronchite, encombrement respiratoire",
      "Fatigue mentale, baisse de concentration",
      "Troubles du foie, digestion lente",
      "Douleurs musculaires",
    ],
    contraindications: [
      "Hypertension, épilepsie",
      "Grossesse et allaitement",
      "Enfants < 6 ans",
      "Pas en usage interne prolongé",
    ],
    internalUse:
      "1 goutte 1×/jour max, 5 jours. Soutien hépatique et digestion uniquement sous contrôle thérapeutique.",
    externalUse:
      "Dilution 10–20%. Massage du dos, thorax, jambes (circulation). Très efficace pour les contractures musculaires.",
  },
  {
    id: "cypres",
    name: "Cyprès",
    latinName: "Cupressus sempervirens",
    emoji: "🌲",
    properties: ["Décongestionnant veineux", "Vasoconstricteur", "Cicatrisant", "Neurotonique", "Immunostimulant"],
    indications: ["Varices, jambes lourdes", "Toux sèche", "Hémorroïdes", "Troubles circulatoires"],
    contraindications: ["Mastoses, cancers hormonodépendants", "Grossesse", "Enfants < 12 ans"],
    internalUse: "Rare — 1 goutte/j max, 3 jours → toux sèche uniquement sous supervision.",
    externalUse:
      "Dilution 20–30%. Massage des jambes (varices, circulation). Application thorax → toux sèche (diluée).",
  },
  {
    id: "sauge",
    name: "Sauge officinale",
    latinName: "Salvia officinalis",
    emoji: "🍃",
    properties: [
      "Emménagogue, régulateur hormonal",
      "Mucolytique",
      "Microbicide",
      "Régulateur de la sudation",
      "Cholagogue + cholérétique",
    ],
    indications: [
      "Sueurs excessives",
      "Troubles hormonaux",
      "Infections ORL",
      "Troubles digestifs d'origine hépatique",
    ],
    contraindications: [
      "⚠️ FORMELLE : Grossesse",
      "⚠️ FORMELLE : Allaitement",
      "⚠️ FORMELLE : Enfants",
      "⚠️ FORMELLE : Cancer hormone-dépendant",
      "Interdite en interne sans supervision (cétones → neurotoxicité)",
    ],
    internalUse: "Généralement interdite. Exception thérapeutique : 1 goutte dans HV, 2 jours max.",
    externalUse: "Dilution stricte 5–10%. Bas-ventre pour cycle, aisselles pour sudation.",
  },
  {
    id: "thym",
    name: "Thym vulgaire",
    latinName: "Thymus vulgaris",
    emoji: "🌿",
    properties: [
      "Anti-infectieux majeur",
      "Stimulant gastro-intestinal",
      "Immunostimulant",
      "Antalgique",
      "Drainant toxines",
    ],
    indications: [
      "Infections pulmonaires",
      "Rhume, grippe",
      "Digestion lente",
      "Fatigue sévère",
      "Infections intestinales",
    ],
    contraindications: ["Enfants < 12 ans", "Grossesse, allaitement", "Hypertension", "Ulcère gastrique"],
    internalUse: "1 goutte 1×/jour dans HV → 5 jours max. Très puissant → toujours sous supervision.",
    externalUse: "Dilution 10% max. Gorge, dos, plexus → infections respiratoires.",
  },
  {
    id: "lavande",
    name: "Lavande vraie",
    latinName: "Lavandula vera",
    emoji: "💜",
    properties: ["Sédative, calmante", "Antiseptique", "Cicatrisante", "Décontractante", "Anti-inflammatoire"],
    indications: [
      "Insomnie, anxiété, crise de nerfs",
      "Piqûres, brûlures légères",
      "Douleurs musculaires",
      "Dermatites, prurit",
    ],
    contraindications: ["Aucune majeure", "Prudence hypotension"],
    internalUse: "1 goutte le soir (miel ou comprimé neutre).",
    externalUse: "Pure sur piqûres ou brûlures. Dilution 20% pour massages. Diffusion 10–20 min.",
  },
  {
    id: "sarriette",
    name: "Sarriette des montagnes",
    latinName: "Satureja montana",
    emoji: "🏔️",
    properties: ["Anti-infectieux majeur", "Antifongique", "Antiparasitaire", "Tonique nerveux", "Antalgique"],
    indications: ["Infections intestinales", "Parasitoses", "Mycoses", "Fatigue sexuelle / intellectuelle"],
    contraindications: ["Interdite pure", "Grossesse, allaitement", "Enfants", "Irritante → prudence peau"],
    internalUse: "1 goutte dans HV → 3 jours max. Intestin, infections sévères (sur avis thérapeute).",
    externalUse: "Dilution 5% max. Abdomen pour digestion. Pieds : mycoses.",
  },
  {
    id: "girofle",
    name: "Clou de girofle",
    latinName: "Eugenia caryophyllata",
    emoji: "🌰",
    properties: ["Bactéricide puissant", "Antivirale", "Antiparasitaire", "Antalgique puissant", "Stimulante générale"],
    indications: [
      "Névralgies dentaires",
      "Infections intestinales",
      "Mycoses",
      "Hépatites virales",
      "Diarrhées infectieuses",
    ],
    contraindications: ["Irritante, dermocaustique", "Grossesse/allaitement", "Ulcères gastriques"],
    internalUse: "1 goutte dans HV → 2 jours max. Soulagement infections intestinales.",
    externalUse: "Dilution 2%. Local dentaire (coton tige). Jamais sur surface étendue.",
  },
  {
    id: "eucalyptus",
    name: "Eucalyptus globulus",
    latinName: "Eucalyptus globulus",
    emoji: "🌿",
    properties: ["Expectorant", "Mucolytique", "Antibactérien", "Antiviral", "Décongestionnant"],
    indications: ["Bronchite", "Rhume, grippe", "Sinusite", "Infections urinaires"],
    contraindications: ["Enfants < 6 ans", "Asthme", "Épilepsie"],
    internalUse: "1 goutte dans miel → 3 jours. Action broncho-pulmonaire.",
    externalUse: "Dilution 20%. Massage thorax / haut du dos. Inhalation 1 goutte eau chaude.",
  },
  {
    id: "basilic",
    name: "Basilic",
    latinName: "Ocimum basilicum",
    emoji: "🌱",
    properties: ["Antispasmodique puissant", "Anti-inflammatoire", "Décongestionnant", "Sédatif"],
    indications: ["Coliques, spasmes digestifs", "Stress", "Troubles hépatiques légers"],
    contraindications: ["Grossesse", "Enfants", "Irritant pur"],
    internalUse: "1 goutte → 2×/jour, 5 jours. Spasmes digestifs.",
    externalUse: "Dilution 5–10%. Abdomen → digestion.",
  },
  {
    id: "carotte",
    name: "Carotte",
    latinName: "Daucus carota",
    emoji: "🥕",
    properties: ["Stimulant hépatorénal", "Régénérant cutané", "Anti-anémique"],
    indications: ["Peau terne, vieillissante", "Fatigue hépatique", "Régénération cellulaire"],
    contraindications: ["Grossesse", "Vitamine A excessive (prudence)"],
    internalUse: "1 goutte/j dans HV. Soutien foie + peau.",
    externalUse: "Dilution 10%. Peau sèche, mature.",
  },
  {
    id: "menthe",
    name: "Menthe poivrée",
    latinName: "Mentha piperita",
    emoji: "🌿",
    properties: ["Analgésique", "Cholagogue", "Tonique général", "Anti-infectieux"],
    indications: ["Migraines", "Nausées, digestion lente", "Fatigue"],
    contraindications: ["Enfants < 6 ans", "Femmes enceintes", "Hypertension"],
    internalUse: "1 goutte dans miel → nausées. 2×/jour max.",
    externalUse: "Dilution 10%. Tempes (jamais yeux). Muscles.",
  },
  {
    id: "petitgrain",
    name: "Oranger amer — Petit grain bigarade",
    latinName: "Citrus aurantium",
    emoji: "🍊",
    properties: ["Calmante, sédative", "Spasmolytique", "Anti-inflammatoire", "Cutanée"],
    indications: ["Stress, anxiété", "Troubles du sommeil", "Affections cutanées"],
    contraindications: ["Aucune majeure", "Prudence hypotension"],
    internalUse: "1 goutte le soir pour anxiété.",
    externalUse: "Dilution 20%. Poignets, plexus, visage.",
  },
  {
    id: "origan",
    name: "Origan",
    latinName: "Thymus capitatus",
    emoji: "🌿",
    properties: ["Anti-infectieux majeur", "Fongicide", "Parasiticide"],
    indications: ["Infections respiratoires sévères", "Infections intestinales", "Amibiases", "Colites"],
    contraindications: ["Très irritant", "Déconseillé pour enfants, grossesse", "Pas d'usage prolongé"],
    internalUse: "1 goutte dans HV → 3 jours. Anti-infectieux puissant.",
    externalUse: "Dilution < 5%. Abdomen, thorax (jamais pur).",
  },
  {
    id: "cannelle",
    name: "Cannelle",
    latinName: "Cinnamomum cassia/ceylon",
    emoji: "🟤",
    properties: ["Anti-infectieux #3", "Antivirale", "Antifongique", "Stimulante"],
    indications: ["Grippe, fièvres", "Infections digestives", "Amibiase", "Fatigue sévère"],
    contraindications: ["Dermocaustique", "Interdite grossesse", "Enfants", "Ulcères"],
    internalUse: "1 goutte dans HV → 2 jours. Anti-infectieux puissant.",
    externalUse: "Dilution 1–2% max. Très irritante → test cutané impératif.",
  },
  {
    id: "geranium",
    name: "Géranium rosat",
    latinName: "Pelargonium graveolens",
    emoji: "🌸",
    properties: ["Antifongique", "Microbicide", "Antalgique", "Astringent", "Tonique veineux"],
    indications: ["Acné, impétigo", "Dermatoses", "Mycoses", "Arthrose"],
    contraindications: ["Aucune majeure", "Possibles allergies cutanées"],
    internalUse: "1 goutte → 2×/jour pour infections cutanées (sur avis).",
    externalUse: "Dilution 10–20%. Parfait pour peaux grasses/acnéiques.",
  },
  {
    id: "myrte",
    name: "Myrte",
    latinName: "Myrtus communis",
    emoji: "🌿",
    properties: ["Expectorante", "Anticatarrhale", "Anti-infectieuse", "Hémostatique"],
    indications: ["Bronchites, sinusites", "Peaux ridées", "Angines"],
    contraindications: ["Grossesse", "Enfants < 6 ans"],
    internalUse: "1 goutte → 3 jours max pour sinusites.",
    externalUse: "Dilution 20%. Massage thorax + visage (ride).",
  },
  {
    id: "pin",
    name: "Pin sylvestre",
    latinName: "Pinus sylvestris",
    emoji: "🌲",
    properties: ["Antiseptique respiratoire", "Stimulant surrénalien", "Antiseptique urinaire"],
    indications: ["Bronchites", "Cystites", "Asthénie"],
    contraindications: ["Hypertension", "Grossesse", "Irritant pur"],
    internalUse: "1 goutte → 2×/jour pour infections urinaires (sur avis).",
    externalUse: "Dilution 20%. Massage thorax, bas du dos.",
  },
  {
    id: "serpolet",
    name: "Serpolet",
    latinName: "Thymus serpyllum",
    emoji: "🌿",
    properties: ["Anti-infectieux moyen", "Antiviral", "Bactéricide", "Stomachique", "Neurotonique"],
    indications: ["Entérocolites", "Flatulences", "Gastralgies", "Infections urinaires hautes"],
    contraindications: ["Grossesse", "Enfants", "Irritant pur"],
    internalUse: "1 goutte → digestion, infections légères.",
    externalUse: "Dilution 5–10%. Abdomen + bas du dos.",
  },
];

export default function AromatherapyLibrary() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-hero py-8 px-4">
      <ProNavigation />

      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
          <Button variant="outline" size="sm" onClick={() => navigate("/treatments-library")}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Bibliothèque de Traitements</span>
            <span className="sm:hidden">Retour</span>
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold text-primary">🌸 Huiles Essentielles</h1>
          <div className="hidden sm:block w-[140px]" />
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">🌸 Huiles Essentielles</CardTitle>
            <CardDescription>
              Guide complet des huiles essentielles avec leurs propriétés, indications, contre-indications et modes
              d'utilisation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 font-medium">⚠️ Précautions générales</p>
              <ul className="text-amber-700 text-sm mt-2 space-y-1">
                <li>• Toujours faire un test cutané avant première utilisation</li>
                <li>• Ne jamais appliquer d'huile essentielle pure sur la peau (sauf exceptions)</li>
                <li>• Respecter les dilutions recommandées</li>
                <li>• Consulter un professionnel pour les enfants, femmes enceintes/allaitantes</li>
                <li>• HV = Huile Végétale (support de dilution)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Essential Oils List */}
        <Card>
          <CardHeader>
            <CardTitle>📚 Liste des Huiles Essentielles ({essentialOils.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {essentialOils.map((oil, index) => (
                <AccordionItem key={oil.id} value={oil.id}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{oil.emoji}</span>
                      <div className="text-left">
                        <span className="font-semibold">
                          {index + 1}. {oil.name}
                        </span>
                        <span className="text-muted-foreground ml-2 text-sm italic">({oil.latinName})</span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pl-10">
                      {/* Propriétés */}
                      <div>
                        <h4 className="font-semibold text-primary mb-2">🔬 Propriétés</h4>
                        <div className="flex flex-wrap gap-2">
                          {oil.properties.map((prop, i) => (
                            <Badge key={i} variant="secondary">
                              {prop}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Indications */}
                      <div>
                        <h4 className="font-semibold text-green-700 mb-2">✅ Indications</h4>
                        <ul className="list-disc list-inside text-sm space-y-1">
                          {oil.indications.map((ind, i) => (
                            <li key={i}>{ind}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Contre-indications */}
                      <div>
                        <h4 className="font-semibold text-red-700 mb-2">⚠️ Contre-indications</h4>
                        <ul className="list-disc list-inside text-sm space-y-1 text-red-600">
                          {oil.contraindications.map((ci, i) => (
                            <li key={i}>{ci}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Usage */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <h4 className="font-semibold text-blue-700 mb-2">💊 Usage interne</h4>
                          <p className="text-sm">{oil.internalUse}</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg">
                          <h4 className="font-semibold text-green-700 mb-2">🧴 Usage externe</h4>
                          <p className="text-sm">{oil.externalUse}</p>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card className="mt-6">
          <CardContent className="py-6">
            <div className="text-center">
              <p className="text-4xl mb-4">🌸</p>
              <p className="text-lg font-medium text-primary">
                L'aromathérapie utilise les huiles essentielles pour leurs propriétés thérapeutiques puissantes.
              </p>
              <p className="text-muted-foreground mt-2">
                Toujours respecter les dosages et contre-indications. En cas de doute, consulter un professionnel de
                santé.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
